import { useState, type FormEvent } from "react";
import { api, errorMessage, isCode } from "../api/client.ts";
import type { RoleItem, RolesData } from "../api/types.ts";
import { CodeInput } from "../components/CodeInput.tsx";
import { Dialog } from "../components/Dialog.tsx";
import { StepUpDialog } from "../components/StepUp.tsx";
import { useToast } from "../components/Toast.tsx";
import { Alert, EmptyState, Spinner } from "../components/ui.tsx";
import { DataTable, type Column } from "../components/DataTable.tsx";
import { Pager } from "../components/Pager.tsx";
import { useClientPages } from "../lib/paging.ts";
import { groupPermissions, PERMISSION_HELP, permissionLabel } from "../lib/permissions.ts";
import { useSession } from "../lib/session.tsx";
import type { Permission } from "../api/types.ts";
import { ROLE_DESC, ROLE_LABEL } from "../lib/format.ts";
import { fmt, useT } from "../i18n/index.tsx";

/** Built-in roles in the console language; custom role names are the merchant's own text. */
export const roleName = (r: { key?: string | null; name: string }) => (r.key && ROLE_LABEL[r.key]) || r.name;
export const roleDescription = (r: { key?: string | null; description?: string | null }) => (r.key && ROLE_DESC[r.key]) || r.description;

type Q = { data: RolesData | null; error: unknown; loading: boolean; reload: () => Promise<void> };

/** "Payouts: View, Create, Approve · Deposits: View" */
function summary(perms: string[], all: string[]) {
  return groupPermissions(all)
    .map((g) => ({ page: g.page, acts: g.perms.filter((p) => perms.includes(p)).map(permissionLabel) }))
    .filter((g) => g.acts.length);
}

export function RolesPanel({ q }: { q: Q }) {
  const t = useT();
  const { can } = useSession();
  const [open, setOpen] = useState<{ mode: "create" | "edit" | "view"; role?: RoleItem } | null>(null);
  const [del, setDel] = useState<RoleItem | null>(null);
  const toast = useToast();
  const all = q.data?.permissions ?? [];
  const manage = can("roles:manage");
  const rp = useClientPages(q.data?.items ?? [], "r_");

  if (q.error) return <Alert tone="error" action={<button className="btn btn-ghost btn-sm" onClick={() => void q.reload()}>{t.common.retry}</button>}>{errorMessage(q.error)}</Alert>;
  if (q.loading && !q.data) return <div className="card"><span className="sk sk-lg" /><span className="sk" /></div>;
  const items = q.data?.items ?? [];
  if (!items.length) return <EmptyState title={t.roles.noRoles} />;
  const cols: Column<RoleItem>[] = [
    {
      key: "name", header: t.roles.colRole, mobile: "title", cell: (r) => (
        <span className="stack-cell">
          <strong className="role-name">{roleName(r)}</strong>
          {roleDescription(r) && <small className="muted">{roleDescription(r)}</small>}
        </span>
      ),
    },
    { key: "type", header: t.roles.colType, cell: (r) => <span className={`pill no-dot ${r.built_in ? "pill-grey" : "pill-blue"}`}>{r.built_in ? t.roles.builtIn : t.roles.custom}</span> },
    { key: "members", header: t.roles.colMembers, align: "right", cell: (r) => <span className="amount">{r.members}</span> },
    {
      key: "perms", header: t.roles.colCanDo, className: "perm-summary", cell: (r) => (
        <span className="perm-lines">
          {summary(r.permissions, all).map((g) => (
            <span key={g.page}><b>{g.page}</b> {g.acts.join(t.roles.listSep)}</span>
          ))}
        </span>
      ),
    },
    {
      key: "act", header: t.roles.colActions, align: "right", cell: (r) => (
        <span className="btn-row tight">
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setOpen({ mode: r.editable && manage ? "edit" : "view", role: r })}>
            {r.editable && manage ? t.common.edit : t.roles.view}
          </button>
          {manage && <button type="button" className="btn btn-sm btn-ghost" onClick={() => setOpen({ mode: "create", role: r })}>{t.roles.duplicate}</button>}
          {manage && r.editable && <button type="button" className="btn btn-sm btn-ghost btn-danger-ghost" onClick={() => setDel(r)}>{t.common.delete}</button>}
        </span>
      ),
    },
  ];

  return (
    <>
      <p className="muted small roles-intro">
        {t.roles.intro}
      </p>
      <DataTable
        caption={t.roles.caption}
        columns={cols}
        rows={rp.rows}
        rowKey={(r) => r.id}
        footer={<Pager page={rp.page} limit={rp.limit} count={rp.rows.length} total={rp.total} onPage={rp.setPage} onLimit={rp.setLimit} />}
      />
      {open && (
        <RoleEditor
          mode={open.mode}
          role={open.role}
          allPermissions={all}
          onClose={() => setOpen(null)}
          onSaved={(r) => { toast(fmt(open.mode === "edit" ? t.roles.saved : t.roles.created, { name: r.name })); void q.reload(); }}
        />
      )}
      <StepUpDialog
        open={!!del}
        onClose={() => setDel(null)}
        title={fmt(t.roles.deleteTitle, { name: del?.name ?? "" })}
        description={t.roles.deleteDesc}
        confirmLabel={t.roles.deleteRole}
        danger
        onConfirm={async (code) => {
          await api.del(`/roles/${encodeURIComponent(del?.id ?? "")}`, { code });
          toast(t.roles.deleted);
          void q.reload();
        }}
      />
    </>
  );
}

/** Create / edit / view a role: name, description and a permission matrix grouped by page. */
export function RoleEditor({ mode, role, allPermissions, onClose, onSaved }: {
  mode: "create" | "edit" | "view"; role?: RoleItem; allPermissions: string[]; onClose: () => void; onSaved: (r: RoleItem) => void;
}) {
  const t = useT();
  const { me } = useSession();
  const copying = mode === "create" && !!role;
  const [name, setName] = useState(copying && role ? fmt(t.roles.copyName, { name: roleName(role) }) : role?.name ?? "");
  const [desc, setDesc] = useState(role?.description ?? "");
  const [perms, setPerms] = useState<string[]>(role?.permissions ?? ["overview:view"]);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const readOnly = mode === "view";
  const groups = groupPermissions(allPermissions);
  const mine = me?.permissions ?? [];

  const toggle = (p: string, on: boolean, group: string[]) => {
    const view = group.find((x) => x.endsWith(":view"));
    setPerms((cur) => {
      let next = on ? [...new Set([...cur, p])] : cur.filter((x) => x !== p);
      // Doing anything on a page needs seeing it; removing "View" removes the page's other actions.
      if (on && view && p !== view) next = [...new Set([...next, view])];
      if (!on && p === view) next = next.filter((x) => !group.includes(x));
      return allPermissions.filter((x) => next.includes(x));
    });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setError(t.roles.nameRequired);
    if (!perms.length) return setError(t.roles.pickPermission);
    if (code.length !== 6) return setCodeError(t.roles.codeRequired);
    setBusy(true);
    setError(null);
    setCodeError(null);
    try {
      const body = { name: name.trim(), description: desc.trim() || undefined, permissions: perms, code };
      const r = mode === "edit" && role
        ? await api.patch<RoleItem>(`/roles/${encodeURIComponent(role.id)}`, body)
        : await api.post<RoleItem>("/roles", body);
      onSaved(r);
      onClose();
    } catch (err) {
      setCode("");
      if (isCode(err, "mfa_required")) setCodeError(t.roles.codeFailed);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const shown = role ? roleName(role) : undefined;
  const title = readOnly ? shown ?? t.roles.role : mode === "edit" ? fmt(t.roles.editTitle, { name: String(shown) }) : copying ? fmt(t.roles.copyTitle, { name: String(shown) }) : t.roles.newRole;
  return (
    <Dialog open onClose={onClose} busy={busy} size="lg" title={title}
      description={readOnly ? (role?.built_in ? t.roles.builtInDesc : t.roles.readOnlyDesc) : t.roles.editDesc}>
      <form className="form-stack" onSubmit={submit} noValidate>
        {!readOnly && (
          <div className="field-row">
            <div className="field">
              <label htmlFor="role-name">{t.roles.name}</label>
              <input id="role-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} autoComplete="off" />
            </div>
            <div className="field">
              <label htmlFor="role-desc">{t.roles.description} <span className="optional">{t.common.optional}</span></label>
              <input id="role-desc" value={desc} onChange={(e) => setDesc(e.target.value)} maxLength={200} autoComplete="off" />
            </div>
          </div>
        )}
        <div className="perm-matrix" role="group" aria-label={t.roles.permissions}>
          {groups.map((g) => (
            <div key={g.page} className="perm-row">
              <div className="perm-page">{g.page}</div>
              <div className="perm-acts">
                {g.perms.map((p) => {
                  const notMine = !mine.includes(p);
                  return (
                    <label key={p} className={`perm-chip ${perms.includes(p) ? "on" : ""} ${readOnly ? "ro" : ""}`}
                      title={notMine && !readOnly ? fmt(t.roles.notMine, { help: PERMISSION_HELP[p as Permission] ?? p }) : PERMISSION_HELP[p as Permission] ?? p}>
                      <input type="checkbox" checked={perms.includes(p)} disabled={readOnly} onChange={(e) => toggle(p, e.target.checked, g.perms)} />
                      <span>{permissionLabel(p)}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        {!readOnly && (
          <>
            <CodeInput value={code} onChange={(v) => { setCode(v); setCodeError(null); }} error={codeError} disabled={busy} />
            {error && <Alert tone="error">{error}</Alert>}
          </>
        )}
        <div className="dlg-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>{readOnly ? t.common.close : t.common.cancel}</button>
          {!readOnly && <button type="submit" className="btn btn-primary" disabled={busy}>{busy && <Spinner />} {mode === "edit" ? t.roles.saveRole : t.roles.createRole}</button>}
        </div>
      </form>
    </Dialog>
  );
}
